import { useState } from "react";
import { DateRangePicker } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import { useDateRange, defaultRange } from "@/lib/use-date-range";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Wallet,
  Wrench,
  TrendingUp,
  Megaphone,
  AlertCircle,
  Phone,
  MessageSquare,
  Bot,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ── Types ────────────────────────────────────────────────────────────────

interface Pipeline {
  ongoing_opps: number;
  ongoing_value_estimate: number | null;
  pending_quote: number;
  quoted_awaiting: number;
}
interface CeoSection {
  total_opps: number;
  accepted: number;
  ongoing: number;
  lost: number;
  win_rate_pct: number;
  accepted_value: number | null;
  avg_deal_size: number | null;
  ad_spend: number | null;
  ad_clicks: number | null;
  ad_impressions: number | null;
  ad_conversions: number | null;
  lsa_spend: number | null;
  lsa_conversions: number | null;
  total_marketing_spend: number | null;
  cash_collected: number | null;
  gross_margin_pct: number | null;
  marketing_efficiency_ratio: number | null;
  pipeline: Pipeline | null;
}
interface CfoSection {
  revenue_by_product: { product: string; accepted_value: number; deals: number }[];
  cash_collected: number | null;
  ar_aging: { bucket: string; total: number; cnt: number }[];
  job_profitability: {
    revenue: number;
    cost: number;
    gross_margin_pct: number;
    jobs_with_data: number;
  } | null;
  supplier_spend: { supplier_spend: number; po_count: number } | null;
  ad_spend: number | null;
  lsa_spend: number | null;
  accepted_value: number | null;
}
interface OperationsSection {
  engineer_utilisation: { engineer: string; scheduled_minutes: number; events: number }[];
  recall: { total_jobs: number; recall_jobs: number; recall_rate_pct: number } | null;
  calls: {
    total_inbound: number;
    missed: number;
    missed_in_office_hours: number;
    avg_ring_sec: number;
    avg_talk_sec: number;
    avoidable_missed: number;
  } | null;
  pipeline_health: {
    pipeline_id: string;
    runs: number;
    failures: number;
    last_run: string;
    last_status: string;
  }[];
}
interface SalesSection {
  lead_to_quote: {
    salesperson: string | null;
    salespersonid: string;
    leads: number;
    quotes_sent: number;
    lead_to_quote_pct: number;
  }[];
  quote_to_sale: {
    salesperson: string | null;
    salespersonid: string;
    quotes_sent: number;
    accepted: number;
    accepted_value: number | null;
    avg_deal_size: number | null;
    quote_to_sale_pct: number;
  }[];
  by_product: {
    salesperson: string | null;
    product: string;
    leads: number;
    quotes: number;
    sales: number;
    accepted_value: number | null;
  }[];
  time_to_quote: { salesperson: string | null; avg_hours_to_quote: number; opps_measured: number }[];
  ai_agent: AiAgent | null;
}
interface AiAgent {
  ai_calls_8x8: number;
  ai_atlas_calls: number;
  ai_keystone_calls: number;
  ai_leads: number;
  ai_bookings: number;
  ai_avg_ux_score: number | null;
}
interface MarketingSection {
  funnel: {
    form_fills: number;
    form_with_gclid: number;
    ad_calls: number;
    inbound_calls: number;
  } | null;
  ad_spend: {
    total_spend: number;
    total_clicks: number;
    total_impressions: number;
    total_conversions: number;
  } | null;
  lsa: {
    lsa_spend: number;
    lsa_clicks: number;
    lsa_conversions: number;
  } | null;
  campaigns: {
    campaign_id: string;
    campaign: string;
    cost: number;
    impressions: number;
    clicks: number;
    click_to_call_conv: number;
    click_to_text_conv: number;
    other_conv: number;
    ad_calls: number;
  }[];
  click_to_call_campaigns: {
    campaign: string;
    ad_calls: number;
    avg_call_duration_sec: number;
    mobile_calls: number;
    manual_calls: number;
  }[];
  click_to_call_attribution: {
    campaign: string;
    ad_calls: number;
    matched_8x8: number;
    matched_opps: number;
    matched_quotes: number;
    matched_sales: number;
    accepted_value: number;
  }[];
  channel_mix: { channel: string; leads: number; with_phone: number; with_email: number }[];
  ai_agent: AiAgent | null;
}
interface Overview {
  sinceDate: string;
  untilDate: string;
  periodDays: number;
  ceo: CeoSection;
  cfo: CfoSection;
  operations: OperationsSection;
  sales: SalesSection;
  marketing: MarketingSection;
  errors?: Record<string, string>;
  dataGaps?: string[];
}

// ── Helpers ──────────────────────────────────────────────────────────────

const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : Math.round(n).toLocaleString();
const fmtGbp = (n: number | null | undefined) =>
  n === null || n === undefined
    ? "—"
    : `£${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${n.toFixed(1)}%`;
const fmtMins = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${Math.round(n / 60)}h`;
const fmtSecs = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : `${n}s`;

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "good" | "bad" | "warn";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600"
      : tone === "bad"
      ? "text-red-600"
      : tone === "warn"
      ? "text-amber-600"
      : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function Management() {
  const { dateRange, setDateRange, from, to } = useDateRange();

  const { data, isLoading, error } = useQuery<Overview>({
    queryKey: ["management-overview", from, to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: from ?? "", to: to ?? "" });
      const res = await fetch(`/api/management/overview?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading)
    return <div className="p-6 text-muted-foreground">Loading management dashboard…</div>;
  if (error || !data)
    return (
      <div className="p-6 text-red-600">
        Failed to load: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );

  return (
    <div className="p-6 space-y-6">
      {/* Header / date picker */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Management Overview</h1>
          <p className="text-sm text-muted-foreground">
            Cross-functional pulse for CEO / CFO / Operations / Sales / Marketing — all live from
            BigQuery
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
        </div>
      </div>

      {data.dataGaps && data.dataGaps.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <div className="font-medium mb-1">Known data gaps for this view:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {data.dataGaps.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="ceo" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ceo">
            <Briefcase className="h-4 w-4 mr-2" />
            CEO
          </TabsTrigger>
          <TabsTrigger value="cfo">
            <Wallet className="h-4 w-4 mr-2" />
            CFO
          </TabsTrigger>
          <TabsTrigger value="operations">
            <Wrench className="h-4 w-4 mr-2" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="sales">
            <TrendingUp className="h-4 w-4 mr-2" />
            Sales
          </TabsTrigger>
          <TabsTrigger value="marketing">
            <Megaphone className="h-4 w-4 mr-2" />
            Marketing
          </TabsTrigger>
        </TabsList>

        {/* ───────────── CEO ───────────── */}
        <TabsContent value="ceo">
          <CeoTab ceo={data.ceo} />
        </TabsContent>

        {/* ───────────── CFO ───────────── */}
        <TabsContent value="cfo">
          <CfoTab cfo={data.cfo} />
        </TabsContent>

        {/* ───────────── Operations ───────────── */}
        <TabsContent value="operations">
          <OperationsTab ops={data.operations} />
        </TabsContent>

        {/* ───────────── Sales ───────────── */}
        <TabsContent value="sales">
          <SalesTab sales={data.sales} />
        </TabsContent>

        {/* ───────────── Marketing ───────────── */}
        <TabsContent value="marketing">
          <MarketingTab marketing={data.marketing} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── CEO ─────────────────────────────────────────────────────────────────

function CeoTab({ ceo }: { ceo: CeoSection }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Accepted £" value={fmtGbp(ceo.accepted_value)} hint="Booked sales" />
        <Kpi
          label="Win rate"
          value={fmtPct(ceo.win_rate_pct)}
          hint={`${ceo.accepted}/${ceo.total_opps} opps`}
          tone={(ceo.win_rate_pct ?? 0) >= 25 ? "good" : (ceo.win_rate_pct ?? 0) >= 15 ? "warn" : "bad"}
        />
        <Kpi label="Avg deal" value={fmtGbp(ceo.avg_deal_size)} />
        <Kpi label="Cash collected" value={fmtGbp(ceo.cash_collected)} />

        <Kpi
          label="Google Ads spend"
          value={fmtGbp(ceo.ad_spend)}
          hint="Enabled campaigns, excl LSA"
        />
        <Kpi
          label="LSA spend"
          value={fmtGbp(ceo.lsa_spend)}
          hint={`${ceo.lsa_conversions ?? 0} charged leads`}
        />
        <Kpi
          label="Mkt efficiency"
          value={
            ceo.marketing_efficiency_ratio ? `${ceo.marketing_efficiency_ratio}×` : "—"
          }
          hint="£ booked ÷ £ total mkt spend"
          tone={
            (ceo.marketing_efficiency_ratio ?? 0) >= 5
              ? "good"
              : (ceo.marketing_efficiency_ratio ?? 0) >= 3
              ? "warn"
              : "bad"
          }
        />
        <Kpi label="Gross margin" value={fmtPct(ceo.gross_margin_pct)} />
        <Kpi
          label="Open pipeline"
          value={fmtInt(ceo.pipeline?.ongoing_opps ?? 0)}
          hint={fmtGbp(ceo.pipeline?.ongoing_value_estimate ?? null)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Pipeline composition (open opps)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">Pending quote</div>
            <div className="font-semibold text-lg">{fmtInt(ceo.pipeline?.pending_quote ?? 0)}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Quoted, awaiting decision</div>
            <div className="font-semibold text-lg">
              {fmtInt(ceo.pipeline?.quoted_awaiting ?? 0)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Total open</div>
            <div className="font-semibold text-lg">{fmtInt(ceo.pipeline?.ongoing_opps ?? 0)}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── CFO ─────────────────────────────────────────────────────────────────

function CfoTab({ cfo }: { cfo: CfoSection }) {
  const totalRev = cfo.revenue_by_product.reduce((s, r) => s + (r.accepted_value || 0), 0);
  const totalAr = cfo.ar_aging.reduce((s, r) => s + (r.total || 0), 0);
  const arOver60 = cfo.ar_aging
    .filter((r) => r.bucket === "61-90d" || r.bucket === "90d+")
    .reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Booked sales" value={fmtGbp(cfo.accepted_value)} />
        <Kpi label="Cash collected" value={fmtGbp(cfo.cash_collected)} />
        <Kpi
          label="Gross margin"
          value={fmtPct(cfo.job_profitability?.gross_margin_pct ?? null)}
          hint={`${cfo.job_profitability?.jobs_with_data ?? 0} jobs costed`}
        />
        <Kpi
          label="Google Ads spend"
          value={fmtGbp(cfo.ad_spend)}
          hint={
            cfo.accepted_value && cfo.ad_spend
              ? `${((cfo.ad_spend / cfo.accepted_value) * 100).toFixed(1)}% of sales`
              : undefined
          }
        />
        <Kpi
          label="LSA spend"
          value={fmtGbp(cfo.lsa_spend)}
          hint="Pay-per-lead, billed separately"
        />
        <Kpi label="Supplier spend" value={fmtGbp(cfo.supplier_spend?.supplier_spend ?? null)} />
        <Kpi
          label="Total AR"
          value={fmtGbp(totalAr)}
          hint={`${arOver60.toLocaleString(undefined, { maximumFractionDigits: 0 })} > 60d`}
          tone={arOver60 / Math.max(totalAr, 1) > 0.2 ? "bad" : arOver60 > 0 ? "warn" : "good"}
        />
        <Kpi
          label="Job revenue (costed)"
          value={fmtGbp(cfo.job_profitability?.revenue ?? null)}
        />
        <Kpi label="Job direct cost" value={fmtGbp(cfo.job_profitability?.cost ?? null)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Revenue by product</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Deals</TableHead>
                  <TableHead className="text-right">Booked £</TableHead>
                  <TableHead className="text-right">% of total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cfo.revenue_by_product.map((r) => (
                  <TableRow key={r.product}>
                    <TableCell>{r.product}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.deals)}</TableCell>
                    <TableCell className="text-right">{fmtGbp(r.accepted_value)}</TableCell>
                    <TableCell className="text-right">
                      {totalRev > 0
                        ? `${((r.accepted_value / totalRev) * 100).toFixed(1)}%`
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Accounts receivable aging</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bucket</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">£ Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cfo.ar_aging.map((r) => (
                  <TableRow key={r.bucket}>
                    <TableCell>{r.bucket}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.cnt)}</TableCell>
                    <TableCell className="text-right">{fmtGbp(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Operations ──────────────────────────────────────────────────────────

function OperationsTab({ ops }: { ops: OperationsSection }) {
  const callMissedPct = ops.calls?.total_inbound
    ? ((ops.calls.missed / ops.calls.total_inbound) * 100).toFixed(1)
    : "—";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Recall rate"
          value={fmtPct(ops.recall?.recall_rate_pct ?? null)}
          hint={`${ops.recall?.recall_jobs ?? 0}/${ops.recall?.total_jobs ?? 0} jobs`}
          tone={
            (ops.recall?.recall_rate_pct ?? 0) <= 2
              ? "good"
              : (ops.recall?.recall_rate_pct ?? 0) <= 5
              ? "warn"
              : "bad"
          }
        />
        <Kpi
          label="Inbound calls"
          value={fmtInt(ops.calls?.total_inbound ?? 0)}
          hint={`${ops.calls?.missed ?? 0} missed (${callMissedPct}%)`}
        />
        <Kpi
          label="Avoidable misses"
          value={fmtInt(ops.calls?.avoidable_missed ?? 0)}
          hint="Mon–Fri 8am–5pm"
          tone={(ops.calls?.avoidable_missed ?? 0) === 0 ? "good" : "bad"}
        />
        <Kpi
          label="Avg ring before pickup"
          value={fmtSecs(ops.calls?.avg_ring_sec ?? null)}
          tone={(ops.calls?.avg_ring_sec ?? 99) < 15 ? "good" : "warn"}
        />
        <Kpi label="Avg call talk-time" value={fmtSecs(ops.calls?.avg_talk_sec ?? null)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Engineer utilisation (top 20)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Engineer</TableHead>
                  <TableHead className="text-right">Events</TableHead>
                  <TableHead className="text-right">Scheduled hrs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ops.engineer_utilisation.map((r) => (
                  <TableRow key={r.engineer}>
                    <TableCell>{r.engineer}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.events)}</TableCell>
                    <TableCell className="text-right">{fmtMins(r.scheduled_minutes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Pipeline / connector health</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pipeline</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead className="text-right">Failures</TableHead>
                  <TableHead>Last status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ops.pipeline_health.map((r) => (
                  <TableRow key={r.pipeline_id}>
                    <TableCell className="font-mono text-xs">{r.pipeline_id}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.runs)}</TableCell>
                    <TableCell
                      className={`text-right ${r.failures > 0 ? "text-red-600 font-semibold" : ""}`}
                    >
                      {fmtInt(r.failures)}
                    </TableCell>
                    <TableCell
                      className={
                        r.last_status === "success" ? "text-emerald-600" : "text-red-600"
                      }
                    >
                      {r.last_status ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Sales ───────────────────────────────────────────────────────────────

function SalesTab({ sales }: { sales: SalesSection }) {
  const ai = sales.ai_agent;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="AI calls (8x8 scored)"
          value={fmtInt(ai?.ai_calls_8x8 ?? 0)}
          hint={`Avg UX ${ai?.ai_avg_ux_score ?? "—"}`}
        />
        <Kpi label="Atlas/AI agent calls" value={fmtInt(ai?.ai_atlas_calls ?? 0)} />
        <Kpi label="AI-handled leads" value={fmtInt(ai?.ai_leads ?? 0)} />
        <Kpi
          label="AI bookings"
          value={fmtInt(ai?.ai_bookings ?? 0)}
          hint={
            ai?.ai_leads
              ? `${((ai.ai_bookings / Math.max(ai.ai_leads, 1)) * 100).toFixed(0)}% lead→booking`
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Lead → Quote — per salesperson
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Salesperson</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Quotes sent</TableHead>
                <TableHead className="text-right">Lead→Quote %</TableHead>
                <TableHead className="text-right">Avg time-to-quote</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sales.lead_to_quote.map((r) => {
                const tt = sales.time_to_quote.find((t) => t.salesperson === r.salesperson);
                const pctTone =
                  r.lead_to_quote_pct >= 70
                    ? "text-emerald-600"
                    : r.lead_to_quote_pct >= 50
                    ? ""
                    : "text-red-600";
                return (
                  <TableRow key={`${r.salespersonid}-ltq`}>
                    <TableCell>{r.salesperson || `(id ${r.salespersonid})`}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.leads)}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.quotes_sent)}</TableCell>
                    <TableCell className={`text-right font-semibold ${pctTone}`}>
                      {fmtPct(r.lead_to_quote_pct)}
                    </TableCell>
                    <TableCell className="text-right">
                      {tt ? `${tt.avg_hours_to_quote}h` : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Quote → Sale — who closes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Salesperson</TableHead>
                <TableHead className="text-right">Quotes sent</TableHead>
                <TableHead className="text-right">Accepted</TableHead>
                <TableHead className="text-right">Quote→Sale %</TableHead>
                <TableHead className="text-right">Accepted £</TableHead>
                <TableHead className="text-right">Avg deal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sales.quote_to_sale.map((r) => {
                const pctTone =
                  r.quote_to_sale_pct >= 40
                    ? "text-emerald-600"
                    : r.quote_to_sale_pct >= 25
                    ? ""
                    : "text-red-600";
                return (
                  <TableRow key={`${r.salespersonid}-qts`}>
                    <TableCell>{r.salesperson || `(id ${r.salespersonid})`}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.quotes_sent)}</TableCell>
                    <TableCell className="text-right">{fmtInt(r.accepted)}</TableCell>
                    <TableCell className={`text-right font-semibold ${pctTone}`}>
                      {fmtPct(r.quote_to_sale_pct)}
                    </TableCell>
                    <TableCell className="text-right">{fmtGbp(r.accepted_value)}</TableCell>
                    <TableCell className="text-right">{fmtGbp(r.avg_deal_size)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Salesperson × Product matrix</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Salesperson</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Quotes</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Accepted £</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sales.by_product.map((r, i) => (
                <TableRow key={`${r.salesperson}-${r.product}-${i}`}>
                  <TableCell>{r.salesperson || "(unknown)"}</TableCell>
                  <TableCell>{r.product}</TableCell>
                  <TableCell className="text-right">{fmtInt(r.leads)}</TableCell>
                  <TableCell className="text-right">{fmtInt(r.quotes)}</TableCell>
                  <TableCell className="text-right">{fmtInt(r.sales)}</TableCell>
                  <TableCell className="text-right">{fmtGbp(r.accepted_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Marketing ───────────────────────────────────────────────────────────

function MarketingTab({ marketing }: { marketing: MarketingSection }) {
  const ai = marketing.ai_agent;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Google Ads spend"
          value={fmtGbp(marketing.ad_spend?.total_spend ?? null)}
          hint="Enabled campaigns, excl LSA"
        />
        <Kpi
          label="LSA spend"
          value={fmtGbp(marketing.lsa?.lsa_spend ?? null)}
          hint={`${marketing.lsa?.lsa_conversions ?? 0} charged leads`}
        />
        <Kpi
          label="Impressions"
          value={fmtInt(marketing.ad_spend?.total_impressions ?? null)}
        />
        <Kpi label="Clicks" value={fmtInt(marketing.ad_spend?.total_clicks ?? null)} />
        <Kpi
          label="Form-fills"
          value={fmtInt(marketing.funnel?.form_fills ?? 0)}
          hint={`${marketing.funnel?.form_with_gclid ?? 0} via Google Ads`}
        />
        <Kpi
          label="Click-to-call (ad ext)"
          value={fmtInt(marketing.funnel?.ad_calls ?? 0)}
          hint="Google Ads call extension"
        />
        <Kpi
          label="8x8 inbound calls"
          value={fmtInt(marketing.funnel?.inbound_calls ?? 0)}
          hint="All inbound via 8x8"
        />
        <Kpi
          label="AI agent calls"
          value={fmtInt((ai?.ai_atlas_calls ?? 0) + (ai?.ai_keystone_calls ?? 0))}
          hint={`UX score ${ai?.ai_avg_ux_score ?? "—"}`}
        />
        <Kpi
          label="AI bookings"
          value={fmtInt(ai?.ai_bookings ?? 0)}
          hint={ai?.ai_leads ? `${ai.ai_leads} leads` : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Megaphone className="h-4 w-4" />
            Per-campaign performance — Click-to-Call vs Click-to-Form
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Impr.</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">📞 C2C conv</TableHead>
                <TableHead className="text-right">📝 C2T conv</TableHead>
                <TableHead className="text-right">Ad calls</TableHead>
                <TableHead className="text-right">Cost / conv</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {marketing.campaigns.map((c) => {
                const totalConv = c.click_to_call_conv + c.click_to_text_conv + c.other_conv;
                const cpc = totalConv > 0 ? c.cost / totalConv : null;
                return (
                  <TableRow key={c.campaign_id}>
                    <TableCell className="font-mono text-xs">{c.campaign}</TableCell>
                    <TableCell className="text-right">{fmtGbp(c.cost)}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.impressions)}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.clicks)}</TableCell>
                    <TableCell className="text-right">
                      {c.click_to_call_conv.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.click_to_text_conv.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(c.ad_calls)}</TableCell>
                    <TableCell className="text-right">{cpc ? fmtGbp(cpc) : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Click-to-Call attribution — ad call → 8x8 inbound → Commusoft opportunity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Ad calls</TableHead>
                <TableHead className="text-right">Matched 8x8</TableHead>
                <TableHead className="text-right">Opps</TableHead>
                <TableHead className="text-right">Quotes</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Accepted £</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {marketing.click_to_call_attribution.map((c) => {
                const matchPct = c.ad_calls > 0 ? (c.matched_8x8 / c.ad_calls) * 100 : 0;
                return (
                  <TableRow key={c.campaign}>
                    <TableCell className="font-mono text-xs">{c.campaign}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.ad_calls)}</TableCell>
                    <TableCell className="text-right">
                      {fmtInt(c.matched_8x8)}{" "}
                      <span className="text-muted-foreground text-xs">
                        ({matchPct.toFixed(0)}%)
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(c.matched_opps)}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.matched_quotes)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {fmtInt(c.matched_sales)}
                    </TableCell>
                    <TableCell className="text-right">{fmtGbp(c.accepted_value)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Click-to-Call detail — by campaign
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Avg dur</TableHead>
                  <TableHead className="text-right">Mobile</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {marketing.click_to_call_campaigns.map((c) => (
                  <TableRow key={c.campaign}>
                    <TableCell className="font-mono text-xs">{c.campaign}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.ad_calls)}</TableCell>
                    <TableCell className="text-right">
                      {fmtSecs(c.avg_call_duration_sec)}
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(c.mobile_calls)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Channel mix — form-fill leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">w/ phone</TableHead>
                  <TableHead className="text-right">w/ email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {marketing.channel_mix.map((c) => (
                  <TableRow key={c.channel}>
                    <TableCell>{c.channel}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.leads)}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.with_phone)}</TableCell>
                    <TableCell className="text-right">{fmtInt(c.with_email)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
